/**
 * The Statistics page's pieces, in the account area's identity: the four key
 * figures, a finding and the one chart beside it, the members' comparison and
 * THE FIGURES' three sortable tabs. The reading is the app's — the same
 * contracts, the same sentences — and only the drawing is this area's: Inter
 * and JetBrains Mono, one ink fill, teal only for where you are, amber
 * (`warnText`) for what is to act on, and no text lighter than `inkSubtle`.
 */
import { useState } from 'react';
import type React from 'react';
import { formatPercent, formatTokens, formatUsd } from '@airlock/contracts/usage/analysis';
import { sparkGeometry, type FigureTables, type KeyFigure } from '@airlock/contracts/usage/figures';
import { stakeWords, type Finding, type FindingChart } from '@airlock/contracts/usage/findings';
import type { MemberSpread } from '@airlock/contracts/usage/members';
import { color, font } from '../../theme/tokens';
import { Spark } from '../../components/Spark';
import { lastSentWords } from './usage-view';

const mono = font.mono;
const AMBER = color.warnText;

/** A period switch, a member picked: the figures and findings enter, faded and 4px low. */
export const enter = (roll: number): string | undefined => (roll ? `${roll % 2 ? 'usageIn' : 'usageFade'} .14s ease` : undefined);

export const KEYFRAMES = `
@keyframes usageIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@keyframes usageFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes usageSkel { 0%, 100% { opacity: .55; } 50% { opacity: 1; } }
@keyframes usageLit { from { background: rgba(31,182,166,.14); } to { background: rgba(0,0,0,.055); } }
`;

/**
 * The strip with a trend (redesign §42.2): each figure its own #F5F5F7 tile,
 * radius 14, 8 apart; the kicker, the figure 28 / 600 with its trend at its
 * right, then two captions — the comparison with the period before (#424245),
 * then what the figure is made of (#515154).
 */
export function KeyFigures({ figures, roll, fresh }: { figures: KeyFigure[]; roll: number; fresh: boolean }): React.JSX.Element {
  return (
    <div role="list" aria-label="Key figures" className="al-quad" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
      {figures.map((k) => (
        <div
          key={k.label}
          role="listitem"
          aria-label={`${k.label} ${k.value}, ${k.line}`}
          style={{
            minWidth: 0,
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            background: color.surfaceAlt,
            borderRadius: 14,
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          <span style={{ fontFamily: mono, fontSize: 10.5, fontWeight: 500, letterSpacing: '.12em', color: color.inkSubtle }}>{k.label}</span>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-.02em', color: color.ink, animation: enter(roll) }}>{k.value}</span>
            {fresh ? null : <Spark values={k.spark} />}
          </div>
          <span style={{ fontSize: 12.5, lineHeight: 1.45, color: color.inkBody }}>{k.line}</span>
          <span style={{ fontSize: 12.5, lineHeight: 1.45, color: color.inkMuted }}>{k.sub}</span>
        </div>
      ))}
    </div>
  );
}

/** A section's head: its name, and one sentence of what it holds. */
export function GroupHead({ title, sub }: { title: string; sub: string }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, paddingBottom: 10, boxShadow: `inset 0 -1px 0 ${color.line}` }}>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: color.ink }}>{title}</h2>
      <span style={{ fontSize: 12.5, color: color.inkSubtle }}>{sub}</span>
    </div>
  );
}

export function FindingGroup({
  title,
  sub,
  items,
  empty,
  act,
  roll,
  readOnly,
  org,
  onVerb
}: {
  title: string;
  sub: string;
  items: Finding[];
  empty: string;
  act: boolean;
  roll: number;
  /** A member: a verb that changes the organization's settings is not theirs to press. */
  readOnly: boolean;
  org: string;
  onVerb: (finding: Finding) => void;
}): React.JSX.Element {
  return (
    <section style={{ display: 'flex', flexDirection: 'column' }}>
      <GroupHead title={title} sub={sub} />
      {items.length ? null : <div style={{ padding: '14px 0', fontSize: 13, lineHeight: 1.5, color: color.inkSubtle }}>{empty}</div>}
      {items.map((f, i) => {
        const settingVerb = f.verb && (f.verb.go === 'workflows' || f.verb.go === 'agent');
        const verb = f.verb && !(readOnly && settingVerb) ? f.verb : null;
        const stake = act ? stakeWords(f) : null;
        return (
          <article
            key={f.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '120px minmax(0, 1fr) 200px',
              gap: '0 20px',
              padding: '16px 0',
              boxShadow: i ? `inset 0 1px 0 ${color.hairSoft}` : 'none',
              animation: enter(roll)
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontFamily: mono, fontSize: 10.5, fontWeight: 500, letterSpacing: '.1em', color: act ? AMBER : color.inkSubtle }}>
                {f.subject}
              </span>
              {stake ? (
                <>
                  <span style={{ fontFamily: mono, fontSize: 12, color: color.inkMuted }}>{stake}</span>
                  <span style={{ fontSize: 12, color: color.inkSubtle }}>at stake</span>
                </>
              ) : null}
            </div>
            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <p style={{ margin: 0, maxWidth: 640, fontSize: 14.5, lineHeight: 1.5, color: color.ink, textWrap: 'pretty' }}>{f.text}</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, lineHeight: 1.45, color: color.inkSubtle, fontVariantNumeric: 'tabular-nums' }}>{f.evidence}</p>
              {f.suggestion || f.verb ? (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  {f.suggestion ? (
                    <span style={{ maxWidth: 500, fontSize: 13.5, lineHeight: 1.45, color: color.inkBody, textWrap: 'pretty' }}>{f.suggestion}</span>
                  ) : null}
                  {verb ? (
                    <button
                      type="button"
                      className="verb"
                      title={verb.tip}
                      onClick={() => onVerb(f)}
                      style={{
                        height: 28,
                        padding: '0 10px',
                        border: `1px solid ${color.line}`,
                        borderRadius: 6,
                        background: 'transparent',
                        fontFamily: 'inherit',
                        fontSize: 12.5,
                        color: color.inkBody,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {verb.label}
                    </button>
                  ) : f.verb ? (
                    <span style={{ fontSize: 12, color: color.inkSubtle }}>Set by {org}’s administrators.</span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div aria-hidden style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 3 }}>
              {f.chart ? <Chart chart={f.chart} act={act} /> : null}
            </div>
          </article>
        );
      })}
    </section>
  );
}

const noteStyle: React.CSSProperties = { fontSize: 12.5, lineHeight: 1.35, color: color.inkSubtle };

function Chart({ chart, act }: { chart: FindingChart; act: boolean }): React.JSX.Element | null {
  if (chart.kind === 'series') {
    const g = sparkGeometry(chart.values, chart);
    if (!g) return null;
    return (
      <>
        <svg width={160} height={36} viewBox="0 0 160 36" style={{ overflow: 'visible' }}>
          {g.refY !== undefined ? <line x1={0} x2={160} y1={g.refY} y2={g.refY} stroke={color.lineInput} strokeDasharray="3 3" /> : null}
          <polyline points={g.points} fill="none" stroke={color.inkSubtle} strokeWidth={1.25} />
          {g.mx !== undefined && g.my !== undefined ? (
            <circle cx={g.mx} cy={g.my} r={3.5} fill={color.accentText} stroke="#FFFFFF" strokeWidth={1.5} />
          ) : null}
        </svg>
        <span style={noteStyle}>{chart.note}</span>
      </>
    );
  }
  if (chart.kind === 'compare') {
    const bar = (width: string): React.JSX.Element => (
      <span style={{ flex: 1, height: 6, borderRadius: 3, background: color.hairSoft }}>
        <span style={{ display: 'block', height: 6, borderRadius: 3, width, background: color.inkSubtle }} />
      </span>
    );
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '80px minmax(0, 1fr)', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: color.inkSubtle }}>{chart.a}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {bar('100%')}
          <span style={{ fontFamily: mono, fontSize: 11.5, color: color.ink }}>{chart.av}</span>
        </span>
        <span style={{ fontSize: 12, color: color.inkSubtle }}>{chart.b}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {bar(`${chart.ratio * 100}%`)}
          <span style={{ fontFamily: mono, fontSize: 11.5, color: color.ink }}>{chart.bv}</span>
        </span>
      </div>
    );
  }
  if (chart.kind === 'strip') {
    return (
      <>
        <div style={{ position: 'relative', height: 22, margin: '0 4px' }}>
          <span style={{ position: 'absolute', left: 0, right: 0, top: 10, height: 1, background: color.lineInput }} />
          <span style={{ position: 'absolute', left: `${chart.mid}%`, top: 3, width: 1, height: 15, background: color.inkSubtle }} />
          {chart.dots.map((d, i) => {
            const size = d.on ? 10 : 7;
            return (
              <span
                key={i}
                style={{
                  position: 'absolute',
                  left: `calc(${d.x}% - ${size / 2}px)`,
                  top: d.on ? 5 : 7,
                  width: size,
                  height: size,
                  borderRadius: 999,
                  background: d.on ? (act ? AMBER : color.ink) : '#C9CCD1'
                }}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: mono, fontSize: 11, color: color.inkSubtle }}>
          <span>{chart.lo}</span>
          <span>| median</span>
          <span>{chart.hi}</span>
        </div>
        <span style={noteStyle}>{chart.note}</span>
      </>
    );
  }
  return (
    <>
      {chart.rows.map((m) => (
        <div key={m.label} style={{ display: 'grid', gridTemplateColumns: '92px minmax(0, 1fr) 56px', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: color.inkSubtle }}>{m.label}</span>
          <span style={{ height: 6, borderRadius: 3, background: color.hairSoft }}>
            <span style={{ display: 'block', height: 6, borderRadius: 3, width: `${m.ratio * 100}%`, background: color.inkSubtle }} />
          </span>
          <span style={{ fontFamily: mono, fontSize: 11.5, color: color.ink, textAlign: 'right' }}>{m.value}</span>
        </div>
      ))}
    </>
  );
}

/* ── the members ───────────────────────────────────────────────────────── */

const MEMBER_COLS = 'minmax(0,2.3fr) minmax(0,.8fr) minmax(0,1fr) minmax(0,.75fr) minmax(0,1.8fr) minmax(0,1.8fr) minmax(0,1.5fr) minmax(0,1.2fr)';
const MEMBER_HEAD: [string, string, boolean?][] = [
  ['member', 'MEMBER', true],
  ['machines', 'MACHINES'],
  ['cost', 'SPENT'],
  ['landed', 'LANDED'],
  ['cpl', 'PER LANDED TASK'],
  ['rate', 'LANDED OF STARTED'],
  ['mostly', 'MOSTLY ON', true],
  ['lastSent', 'LAST SENT', true]
];

/** One row per member: each ratio a dot on a line beside everyone's median, never a rank. */
export function MemberTable({ spread, onOpen }: { spread: MemberSpread; onOpen: (email: string) => void }): React.JSX.Element {
  const [[key, dir], setSort] = useState<[string, number]>(['member', 1]);
  const rows = [...spread.rows].sort((a, b) => {
    const A = (a as unknown as Record<string, unknown>)[key];
    const B = (b as unknown as Record<string, unknown>)[key];
    if (typeof A === 'string' || typeof B === 'string') return String(A ?? '').localeCompare(String(B ?? '')) * dir;
    return ((typeof A === 'number' ? A : -1) - (typeof B === 'number' ? B : -1)) * dir;
  });
  // A point on a scale (redesign §42.5): the value in mono, then an 88 × 12
  // scale — a 1 px #E0E0E5 track, the median a 1 × 10 #86868B tick, the
  // person an 8 px ink dot — named in words, since the dot is not read.
  const ratio = (value: string, ink: string, x: number | null, mid: number, median: string): React.JSX.Element => (
    <span style={{ minWidth: 0, padding: '0 10px', display: 'grid', gridTemplateColumns: '60px 88px', justifyContent: 'start', gap: 10, alignItems: 'center' }}>
      <span style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: mono, fontSize: 12.5, color: ink }}>{value}</span>
      <span role="img" aria-label={`${value}, median ${median}`} style={{ position: 'relative', width: 88, height: 12 }}>
        <span style={{ position: 'absolute', left: 0, right: 0, top: 6, height: 1, background: color.line }} />
        <span style={{ position: 'absolute', left: `${mid}%`, top: 1, width: 1, height: 10, background: color.lineInput }} />
        {x !== null ? (
          <span style={{ position: 'absolute', left: `calc(${x}% - 4px)`, top: 2, width: 8, height: 8, borderRadius: 999, background: color.ink }} />
        ) : null}
      </span>
    </span>
  );
  const num: React.CSSProperties = { padding: '0 10px', textAlign: 'right', fontFamily: mono, fontSize: 12.5, color: color.inkBody };
  return (
    <div role="table" aria-label="Members" style={{ display: 'flex', flexDirection: 'column', marginTop: 12, border: `1px solid ${color.line}`, borderRadius: 9, background: color.surface, overflow: 'hidden' }}>
      <div role="row" style={{ display: 'grid', gridTemplateColumns: MEMBER_COLS, height: 36, alignItems: 'center', background: color.surfaceAlt, boxShadow: `inset 0 -1px 0 ${color.line}` }}>
        {MEMBER_HEAD.map(([k, label, left]) => (
          <SortHead key={k} label={label} left={!!left} on={k === key} dir={dir} onSort={() => setSort([k, k === key ? -dir : left ? 1 : -1])} />
        ))}
      </div>
      {rows.map((r) => (
        <div
          key={r.member}
          role="row"
          tabIndex={0}
          className="usage-row"
          aria-label={`${r.member}: ${formatUsd(r.cost)}, ${r.landed} landed`}
          onClick={() => onOpen(r.member)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onOpen(r.member);
          }}
          style={{ display: 'grid', gridTemplateColumns: MEMBER_COLS, minHeight: 48, alignItems: 'center', boxShadow: `inset 0 -1px 0 ${color.hairSoft}`, cursor: 'pointer', outline: 'none' }}
        >
          <span style={{ minWidth: 0, padding: '0 10px', fontFamily: mono, fontSize: 12.5, color: color.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.member}</span>
          <span style={num}>{r.machines}</span>
          <span style={num}>{formatUsd(r.cost)}</span>
          <span style={num}>{r.landed}</span>
          {ratio(r.cpl === null ? '—' : formatUsd(r.cpl), r.cpl === null ? color.inkSubtle : color.inkBody, r.cplX, spread.cplMidX, spread.cplMid === null ? 'unknown' : formatUsd(spread.cplMid))}
          {ratio(r.rate === null ? (r.started ? 'too few' : '—') : formatPercent(r.rate), r.rate === null ? color.inkSubtle : color.inkBody, r.rateX, spread.rateMidX, spread.rateMid === null ? 'unknown' : formatPercent(spread.rateMid))}
          <span style={{ minWidth: 0, padding: '0 10px', fontFamily: mono, fontSize: 12.5, color: color.inkBody, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.mostly ?? '—'}</span>
          <span style={{ padding: '0 10px', fontSize: 12.5, color: color.inkMuted, whiteSpace: 'nowrap' }}>{lastSentWords(r.lastSent)}</span>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: 10, fontSize: 12, color: color.inkSubtle, background: color.surfaceAlt }}>
        <span>
          Per landed task: $0 → {formatUsd(spread.cplTop)} · median {spread.cplMid === null ? '—' : formatUsd(spread.cplMid)}
        </span>
        <span>Landed of started: 0 → 100% · median {spread.rateMid === null ? '—' : formatPercent(spread.rateMid)}</span>
        <span>Fewer than 3 tasks started: “too few”</span>
      </div>
    </div>
  );
}

function SortHead({ label, left, on, dir, onSort }: { label: string; left: boolean; on: boolean; dir: number; onSort: () => void }): React.JSX.Element {
  return (
    <span role="columnheader" aria-sort={on ? (dir < 0 ? 'descending' : 'ascending') : 'none'} style={{ minWidth: 0, display: 'flex', justifyContent: left ? 'flex-start' : 'flex-end' }}>
      <button
        type="button"
        onClick={onSort}
        className="usage-sort"
        style={{
          minHeight: 26,
          minWidth: 0,
          padding: '2px 8px',
          border: 0,
          borderRadius: 6,
          background: 'transparent',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          textAlign: left ? 'left' : 'right',
          lineHeight: 1.25,
          fontFamily: mono,
          fontSize: 10.5,
          fontWeight: 500,
          letterSpacing: '.08em',
          color: on ? color.ink : color.inkSubtle,
          cursor: 'pointer'
        }}
      >
        {label}
        <span aria-hidden style={{ width: 8, opacity: on ? 1 : 0, transition: 'opacity .14s' }}>
          {on && dir > 0 ? '↑' : '↓'}
        </span>
      </button>
    </span>
  );
}

/* ── the figures ───────────────────────────────────────────────────────── */

export type FigureTab = 'pair' | 'kind' | 'day';
const TABS: [FigureTab, string][] = [
  ['pair', 'By model · effort'],
  ['kind', 'By kind of step'],
  ['day', 'By day']
];

interface Cell {
  v: string;
  left?: boolean;
  strong?: boolean;
  ui?: boolean;
  flag?: string | null;
  note?: string | null;
}

const dash = (v: number | null, f: (n: number) => string): string => (v === null ? '—' : f(v));
const S = (v: string, left = false): Cell => ({ v, strong: true, left });

function tables(t: FigureTables): Record<FigureTab, { aria: string; cols: string; head: [string, string, boolean?][]; rows: { key: string; sort: Record<string, unknown>; cells: Cell[] }[]; total: Cell[] }> {
  const T = t.totals;
  return {
    pair: {
      aria: 'By model and effort',
      cols: 'minmax(0,2fr) repeat(3,minmax(0,.8fr)) minmax(0,.9fr) repeat(2,minmax(0,.75fr)) minmax(0,1.2fr) minmax(0,1.3fr) minmax(0,.9fr)',
      head: [['label', 'MODEL · EFFORT', true], ['turns', 'TURNS'], ['tokens', 'TOKENS'], ['cache', 'CACHE READ'], ['cost', 'SPENT'], ['started', 'STARTED'], ['landed', 'LANDED'], ['rate', 'LANDED OF STARTED'], ['cpl', 'PER LANDED'], ['reruns', 'ASKED AGAIN']],
      rows: t.pairs.map((r) => ({
        key: r.key,
        sort: { ...r },
        cells: [
          { v: r.label, left: true, strong: true },
          { v: String(r.turns) },
          { v: formatTokens(r.tokens) },
          { v: dash(r.cache, formatPercent) },
          { v: formatUsd(r.cost) },
          { v: String(r.started) },
          { v: String(r.landed) },
          r.few ? { v: '—', note: 'too few' } : { v: dash(r.rate, formatPercent) },
          { v: dash(r.cpl, formatUsd), flag: r.flag },
          { v: String(r.reruns) }
        ]
      })),
      total: [S('All', true), S(String(T.turns)), S(formatTokens(T.tokens)), S(dash(T.cache, formatPercent)), S(formatUsd(T.cost)), S(String(T.started)), S(String(T.landed)), S(dash(T.rate, formatPercent)), S(dash(T.cpl, formatUsd)), S(String(T.reruns))]
    },
    kind: {
      aria: 'By kind of step',
      cols: 'minmax(0,2.4fr) repeat(5,minmax(0,1fr)) minmax(0,1.5fr)',
      head: [['label', 'KIND OF WORK', true], ['turns', 'TURNS'], ['cost', 'SPENT'], ['share', 'SHARE OF SPEND'], ['steps', 'STEPS PASSED'], ['again', 'RAN AGAIN'], ['mostly', 'MOSTLY ON', true]],
      rows: t.kinds.map((r) => ({
        key: r.key,
        sort: { ...r, steps: r.stepsPassed },
        cells: [
          { v: r.label, left: true, ui: true, note: r.sub },
          { v: String(r.turns) },
          { v: formatUsd(r.cost) },
          { v: formatPercent(r.share) },
          { v: r.steps ?? '—' },
          { v: r.again === null ? '—' : String(r.again) },
          { v: r.mostly, left: true }
        ]
      })),
      total: [S('All', true), S(String(T.turns)), S(formatUsd(T.cost)), S('100%'), S(''), S(''), S('')]
    },
    day: {
      aria: 'By day',
      cols: 'minmax(0,1.6fr) repeat(4,minmax(0,1fr)) minmax(0,1.4fr) repeat(2,minmax(0,1fr))',
      head: [['day', 'DAY', true], ['cost', 'SPENT'], ['turns', 'TURNS'], ['started', 'STARTED'], ['landed', 'LANDED'], ['cpl', 'PER LANDED'], ['reruns', 'ASKED AGAIN'], ['conflicts', 'CONFLICTS']],
      rows: t.days.map((r) => ({
        key: r.key,
        sort: { ...r, day: r.key },
        cells: [
          { v: r.label, left: true, strong: true },
          { v: formatUsd(r.cost) },
          { v: String(r.turns) },
          { v: String(r.started) },
          { v: String(r.landed) },
          { v: dash(r.cpl, formatUsd), flag: r.flag },
          { v: String(r.reruns) },
          { v: String(r.conflicts) }
        ]
      })),
      total: [S('All', true), S(formatUsd(T.cost)), S(String(T.turns)), S(String(T.started)), S(String(T.landed)), S(dash(T.cpl, formatUsd)), S(String(T.reruns)), S(String(T.conflicts))]
    }
  };
}

const START: Record<FigureTab, [string, number]> = { pair: ['cost', -1], kind: ['cost', -1], day: ['day', -1] };

export function FigureTabs({ data, tab, onTab, lit, litKey }: { data: FigureTables; tab: FigureTab; onTab: (tab: FigureTab) => void; lit: string | null; litKey: number }): React.JSX.Element {
  const [sort, setSort] = useState(START);
  const D = tables(data)[tab];
  const [sk, sd] = sort[tab];
  const rows = [...D.rows].sort((a, b) => {
    const A = a.sort[sk];
    const B = b.sort[sk];
    if (typeof A === 'string' || typeof B === 'string') return String(A ?? '').localeCompare(String(B ?? '')) * sd;
    return ((typeof A === 'number' ? A : -1) - (typeof B === 'number' ? B : -1)) * sd;
  });
  const cell = (c: Cell, i: number, total = false): React.JSX.Element => (
    <span
      key={i}
      role="cell"
      style={{
        minWidth: 0,
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: c.left ? 'flex-start' : 'flex-end',
        gap: 6,
        padding: '0 8px',
        fontFamily: c.ui ? font.sans : mono,
        fontSize: c.ui ? 13 : 12.5,
        fontWeight: c.strong ? 500 : 400,
        color: c.strong || c.ui || total ? color.ink : color.inkBody,
        whiteSpace: 'nowrap',
        overflow: 'hidden'
      }}
    >
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.v}</span>
      {c.flag ? <span style={{ flex: 'none', fontFamily: mono, fontSize: 11, color: AMBER }}>{c.flag}</span> : null}
      {c.note ? <span style={{ flex: 'none', fontFamily: font.sans, fontSize: 11.5, color: color.inkSubtle }}>{c.note}</span> : null}
    </span>
  );
  return (
    <section style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, boxShadow: `inset 0 -1px 0 ${color.line}` }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 600, color: color.ink }}>The figures</h2>
        {/* Table tabs (redesign §42.4): 40 high, 13.5, the chosen one ink 600
            over a 2 px ink underline, colour and underline eased over 150 ms;
            the list scrolls on its own on a phone. */}
        <div role="tablist" aria-label="The figures" style={{ display: 'flex', gap: 4, minWidth: 0, overflowX: 'auto' }}>
          {TABS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={k === tab}
              onClick={() => onTab(k)}
              className="usage-tab"
              style={{
                flex: 'none',
                height: 40,
                padding: '0 10px',
                border: 0,
                background: 'transparent',
                boxShadow: `inset 0 -2px 0 ${k === tab ? color.ink : 'transparent'}`,
                fontFamily: 'inherit',
                fontSize: 13.5,
                fontWeight: k === tab ? 600 : 400,
                color: k === tab ? color.ink : color.inkMuted,
                cursor: 'pointer',
                transition: 'box-shadow 150ms ease, color 150ms ease'
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div role="table" aria-label={D.aria} style={{ display: 'flex', flexDirection: 'column' }}>
        <div role="row" style={{ display: 'grid', gridTemplateColumns: D.cols, minHeight: 34, alignItems: 'center', boxShadow: `inset 0 -1px 0 ${color.hairSoft}` }}>
          {D.head.map(([k, label, left]) => (
            <SortHead key={k} label={label} left={!!left} on={k === sk} dir={sd} onSort={() => setSort({ ...sort, [tab]: [k, k === sk ? -sd : left ? 1 : -1] })} />
          ))}
        </div>
        {rows.slice(0, 30).map((r) => {
          const on = lit !== null && r.key === lit;
          return (
            <div
              key={on ? `${r.key}:${litKey}` : r.key}
              role="row"
              aria-selected={on || undefined}
              className="usage-row"
              style={{
                display: 'grid',
                gridTemplateColumns: D.cols,
                minHeight: 32,
                alignItems: 'center',
                // The row a « Show the day » picked (§42.4): selected .055 and a
                // 3 px teal rule at its left.
                background: on ? 'rgba(0,0,0,.055)' : 'transparent',
                boxShadow: `${on ? `inset 3px 0 0 ${color.accentText}, ` : ''}inset 0 -1px 0 ${color.hairSoft}`,
                animation: on ? 'usageLit .18s ease' : undefined
              }}
            >
              {r.cells.map((c, i) => cell(c, i))}
            </div>
          );
        })}
        {rows.length > 30 ? (
          <div style={{ height: 32, display: 'flex', alignItems: 'center', padding: '0 8px', fontFamily: mono, fontSize: 11.5, color: color.inkSubtle, boxShadow: `inset 0 -1px 0 ${color.hairSoft}` }}>
            + {rows.length - 30} more · {rows.length} in all
          </div>
        ) : null}
        <div role="row" style={{ display: 'grid', gridTemplateColumns: D.cols, minHeight: 34, alignItems: 'center', boxShadow: `inset 0 1px 0 ${color.line}` }}>
          {D.total.map((c, i) => cell(c, i, true))}
        </div>
      </div>
    </section>
  );
}
